import { useCallback, useContext, useMemo } from "react";

import { ArrowSquareOut, CopySimple } from "@magnetic/icons";
import { Text } from "@magnetic/text";
import { Tooltip } from "@magnetic/tooltip";

import { Button } from "src/components/buttons";
import { HFlex } from "src/components/hFlex";
import { PATHS } from "src/core/constants";
import { DataProviderContext } from "src/core/provider/dataProvider";
import { TimeZoneContext } from "src/core/timeZoneProvider";
import { FORMAT } from "src/dataTypes/dateFormats";

import "./mockShellItemHeader.scss";

interface MockShellItemHeaderProps {
  readonly command: string;
  readonly executedAt: Date | undefined;
  readonly onCopy?: () => void;
  readonly showCopyButton?: boolean;
  readonly showPopOutButton?: boolean;
}

export const MockShellItemHeader = ({
  command,
  executedAt,
  onCopy,
  showCopyButton = true,
  showPopOutButton = true
}: MockShellItemHeaderProps) => {
  const { fabric } = useContext(DataProviderContext);
  const { getFormattedDate } = useContext(TimeZoneContext);

  const popOutUrl = useMemo(() => {
    if (!fabric?.id || !command) return "";
    const encodedCommand = encodeURIComponent(command);
    return `/${PATHS.FABRIC}/${fabric.id}/${PATHS.MOCK_SHELL}?command=${encodedCommand}`;
  }, [fabric?.id, command]);

  const handlePopOut = useCallback(() => {
    if (popOutUrl) {
      window.open(popOutUrl, "_blank");
    }
  }, [popOutUrl]);

  return (
    <HFlex
      justify="space-between"
      align="center"
      className="mock-shell-history-item-header"
    >
      <HFlex gap="sm" align="center">
        <Text size="p5" className="mock-shell-history-subcommand">
          {`${fabric?.name}# `}
        </Text>
        <Text weight="semi-bold" className="mock-shell-history-command">
          {command}
        </Text>
      </HFlex>
      <HFlex gap="sm" align="center">
        <Text size="p5" className="mock-shell-history-subcommand">
          Executed at {getFormattedDate(executedAt, FORMAT.LONG)}
        </Text>
        {showPopOutButton && (
          <Tooltip title="Open command in new window">
            <Button
              name="pop-out-command"
              kind="tertiary"
              size="sm"
              icon={<ArrowSquareOut />}
              onClick={handlePopOut}
            />
          </Tooltip>
        )}
        {showCopyButton && onCopy && (
          <Tooltip title="Copy output">
            <Button
              name="copy-output"
              kind="tertiary"
              size="sm"
              icon={<CopySimple />}
              onClick={onCopy}
            >
              Copy output
            </Button>
          </Tooltip>
        )}
      </HFlex>
    </HFlex>
  );
};
